import { useEffect, useRef, useState, useCallback } from 'react';
import mqttService from '../services/mqttService';

/**
 * Hook to subscribe to robot path updates via MQTT.
 * Subscribes to topic: cloud/{robotId}/robotPath
 *
 * @param {string} robotId - The robot ID to subscribe path updates for
 * @returns {{ robotPath: Array, isConnected: boolean }}
 *   - robotPath: array of [lng, lat] coordinate pairs for the map
 *   - isConnected: whether the MQTT connection is active
 */
export const useRobotPathMqtt = (robotId) => {
  const [robotPath, setRobotPath] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const callbackRef = useRef(null);
  const topicRef = useRef(null);

  // Stable connect function
  const connectMqtt = useCallback(() => {
    mqttService.connect(
      () => setIsConnected(true),
      (error) => {
        console.error('MQTT: Connection error', error);
        setIsConnected(false);
      }
    );

    const connectionListener = (connected) => setIsConnected(connected);
    mqttService.addConnectionListener(connectionListener);

    return connectionListener;
  }, []);

  useEffect(() => {
    if (!robotId) return;

    // Connect to MQTT broker
    const connectionListener = connectMqtt();

    const topic = `cloud/${robotId}/robotPath`;
    topicRef.current = topic;

    // Create callback for handling incoming path messages
    const callback = (message, receivedTopic) => {
      try {
        let pathData;
        if (typeof message === 'string') {
          pathData = JSON.parse(message);
        } else if (typeof message === 'object') {
          pathData = message;
        } else {
          console.error(`MQTT: Invalid message format for topic '${receivedTopic}'`);
          return;
        }

        // Extract path array and convert {lng, lat} objects to [lng, lat] pairs
        const path = pathData?.path;
        if (Array.isArray(path) && path.length > 0) {
          const coordinates = path.map((point) => [point.lng, point.lat]);
          setRobotPath(coordinates);
        }
      } catch (error) {
        console.error(`MQTT: Error parsing robot path message:`, error);
      }
    };

    callbackRef.current = callback;
    mqttService.subscribe(topic, callback, { qos: 1 });

    // Cleanup on unmount or robotId change
    return () => {
      if (topicRef.current && callbackRef.current) {
        mqttService.unsubscribe(topicRef.current, callbackRef.current);
      }
      mqttService.removeConnectionListener(connectionListener);
      topicRef.current = null;
      callbackRef.current = null;
    };
  }, [robotId, connectMqtt]);

  return { robotPath, isConnected };
};

export default useRobotPathMqtt;

